import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
  static readonly values = {
    name: String,
    fallbackTargetSelector: String,
  };

  declare readonly nameValue: string;
  declare readonly hasNameValue: boolean;
  declare readonly fallbackTargetSelectorValue: string;
  declare readonly hasFallbackTargetSelectorValue: boolean;

  connect() {
    this.element.addEventListener("scrollend", this._onScrollEnd);

    const cachedScrollTop = this._getCachedScrollTop();
    const fallbackTarget = this._getFallbackTarget();

    // Case 1: A cached position exists. This is the highest priority.
    if (cachedScrollTop !== null) {
      if (
        fallbackTarget &&
        !this._isElementVisibleAt(fallbackTarget, cachedScrollTop)
      ) {
        // Fallback is not visible at the cached position, so scroll to it minimally.
        this._scrollFallbackIntoView(fallbackTarget, cachedScrollTop);
      } else {
        this.element.scrollTop = cachedScrollTop;
      }
      return;
    }

    if (fallbackTarget) {
      // No cache, so scroll to fallback only if it's not already visible.
      if (!this._isElementFullyVisible(fallbackTarget)) {
        this._scrollFallbackIntoView(fallbackTarget, null);
      }
    }
  }

  disconnect() {
    this.element.removeEventListener("scrollend", this._onScrollEnd);
  }

  private readonly _onScrollEnd = () => {
    if (this.hasNameValue) {
      sessionStorage.setItem(this.nameValue, this.element.scrollTop.toString());
    }
  };

  /**
   * Reads from sessionStorage and returns a number or null for cleaner checks.
   */
  private _getCachedScrollTop(): number | null {
    if (!this.hasNameValue) return null;

    const cachedValue = sessionStorage.getItem(this.nameValue);
    return cachedValue ? parseInt(cachedValue, 10) : null;
  }

  /**
   * Finds and returns the fallback target element or null.
   */
  private _getFallbackTarget(): HTMLElement | null {
    if (!this.hasFallbackTargetSelectorValue) return null;

    return this.element.querySelector<HTMLElement>(
      this.fallbackTargetSelectorValue
    );
  }

  /**
   * "Dry Run" Check: Calculates if a target would be visible at a given scrollTop value
   * without actually scrolling. This prevents visual flicker.
   */
  private _isElementVisibleAt(
    fallbackTarget: HTMLElement,
    scrollTop: number
  ): boolean {
    const container = this.element;
    const containerHeight = container.clientHeight;

    // Calculate position of the target RELATIVE to the scrollable container's content area.
    const targetTop = fallbackTarget.offsetTop - container.offsetTop;
    const targetBottom = targetTop + fallbackTarget.offsetHeight;

    return (
      targetTop >= scrollTop && targetBottom <= scrollTop + containerHeight
    );
  }

  /**
   * Live Check: Checks if a target is currently fully visible in the container's viewport.
   */
  private _isElementFullyVisible(fallbackTarget: HTMLElement): boolean {
    const containerRect = this.element.getBoundingClientRect();
    const targetRect = fallbackTarget.getBoundingClientRect();
    return (
      targetRect.top >= containerRect.top &&
      targetRect.bottom <= containerRect.bottom
    );
  }

  /**
   * Scrolls the fallback target into view with minimal movement.
   */
  private _scrollFallbackIntoView(
    fallbackTarget: HTMLElement,
    scrollTop: number | null
  ) {
    const container = this.element;
    const containerHeight = container.clientHeight;
    const targetTop = fallbackTarget.offsetTop - container.offsetTop;
    const targetBottom = targetTop + fallbackTarget.offsetHeight;

    if (scrollTop == null || targetTop < scrollTop) {
      fallbackTarget.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    if (targetBottom > scrollTop + containerHeight) {
      fallbackTarget.scrollIntoView({ behavior: "auto", block: "end" });
    }
  }
}
